import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { t } from '../../localization';
import isDomainSearchQueryValid from '../helpers/is-domain-search-query-valid';
import { mockedDomainsData } from '../constants/common';

const { DomainSelectorListRow, Stack, DomainSelectorConnectThirdParty, DomainSelectorEmptyState } =
  designSystem;

const getCaptions = () => ({
  connectThirdParty: {
    title: t('Connect a domain purchased elsewhere'),
    inputPlaceholder: 'yourdomain.com',
    actionButtonLabel: t('Connect'),
  },
  errorMessage: t('Please enter a valid domain name'),
  emptyState: {
    title: t(
      'Oops! Looks like you do not have any domains added to your Yola account. Once you have purchased a domain from Yola, it will appear here'
    ),
    actionButtonLabel: t('Buy new domain'),
  },
});

const PurchasedDomainsSelectorContainer = ({
  domains: purchasedDomains = [],
  isLoading = false,
  onDomainSelect,
  onConnectThirdPartyDomainClick,
  onEmptyStateButtonClick,
}) => {
  const [domainName, setDomainName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const captions = getCaptions();

  const purchasedDomainList = useMemo(
    () => {
      const domains = isLoading ? mockedDomainsData : purchasedDomains;

      return domains.map(({ domain, available }) => ({
        domain,
        captions: {
          title: domain,
          price: available ? '' : t('In use'),
          button: t('Use'),
        },
        disabled: !available,
        ...(!available && { priceAppearance: 'low-emphasis' }),
      }));
    },
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    [isLoading]
  );

  const validateThirdPartyDomain = useCallback((domain) => {
    if (!isDomainSearchQueryValid(domain)) {
      setErrorMessage(captions.errorMessage);
      return false;
    }

    setErrorMessage('');
    return true;
  }, [captions.errorMessage]);

  const handleChange = useCallback((inputValue) => {
    setDomainName(inputValue);
    validateThirdPartyDomain(inputValue);
  }, [validateThirdPartyDomain]);

  const handleBlur = useCallback(() => {
    validateThirdPartyDomain(domainName);
  }, [validateThirdPartyDomain, domainName]);

  const handleConnectThirdPartyDomainClicked = useCallback(() => {
    if (!validateThirdPartyDomain(domainName)) {
      return;
    }

    onConnectThirdPartyDomainClick(domainName);
  }, [onConnectThirdPartyDomainClick, domainName, validateThirdPartyDomain]);

  return (
    <div className="ws-b2c-domain-selector__purchased-domains">
      <Stack gap={purchasedDomainList.length ? 'spacing-3-xs' : 'spacing-m'}>
        {!isLoading && !purchasedDomainList.length && (
          <DomainSelectorEmptyState
            title={captions.emptyState.title}
            actionButtonLabel={captions.emptyState.actionButtonLabel}
            onClick={onEmptyStateButtonClick}
          />
        )}
        {!!purchasedDomainList.length && (
          <div>
            {purchasedDomainList.map((item) => (
              <DomainSelectorListRow
                appearance="default"
                isLoading={isLoading}
                captions={item.captions}
                disabled={item.disabled}
                key={item.domain}
                priceAppearance={item.priceAppearance}
                onClick={() => onDomainSelect(item.domain)}
              />
            ))}
          </div>
        )}
        <DomainSelectorConnectThirdParty
          value={domainName}
          captions={captions.connectThirdParty}
          errorMessage={errorMessage}
          onButtonClick={handleConnectThirdPartyDomainClicked}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Stack>
    </div>
  );
};

PurchasedDomainsSelectorContainer.propTypes = {
  domains: PropTypes.arrayOf(
    PropTypes.shape({ domain: PropTypes.string, available: PropTypes.bool })
  ),
  isLoading: PropTypes.bool,
  onDomainSelect: PropTypes.func.isRequired,
  onConnectThirdPartyDomainClick: PropTypes.func.isRequired,
  onEmptyStateButtonClick: PropTypes.func.isRequired,
};

export default PurchasedDomainsSelectorContainer;
